import type {
	RankingControl,
	RankingControlExcluded,
	RankingControlNotAdmitted
} from '@repo/sdk/gen'
import { Badge } from '@repo/ui/badge'
import { DataTable } from '@repo/ui/components/data-table/data-table'
import type {
	DataTableColumn,
	DataTablePaginationConfig
} from '@repo/ui/components/data-table/types'
import { useTranslation } from '@/lib/i18n'
import { formatRomeMoment } from '../../../-lib/rome-time'
import { Name } from './ranking-list'

type NotAdmitted = RankingControlNotAdmitted

/** For a list read a page at a time: the pages, and whether the next is loading. */
interface Paging {
	pagination?: DataTablePaginationConfig
	loading?: boolean
}

const QUOTAS = ['q30', 'q70'] as const

/**
 * Who is not admitted to any quota, with the reason for each quota, in the
 * shared table. With `residenceId` a name opens the application.
 */
function NotAdmittedRows({
	items,
	residenceId,
	pagination,
	loading
}: {
	items: NotAdmitted[]
	residenceId?: string
} & Paging) {
	const { t } = useTranslation()
	if (!pagination && items.length === 0) {
		return (
			<p className='text-muted-foreground text-sm'>
				{t('admin-ranking:not_admitted.empty')}
			</p>
		)
	}
	const columns: DataTableColumn<NotAdmitted & { id: string }>[] = [
		{
			id: 'name',
			header: t('admin-ranking:columns.name'),
			cell: (item) => <Name residenceId={residenceId} row={item} />,
			minWidth: 200,
			flex: 2
		},
		{
			id: 'protocol',
			header: t('admin-ranking:columns.protocol'),
			cell: (item) => (
				<span className='tabular-nums'>{item.protocol}</span>
			),
			minWidth: 130,
			flex: 1
		},
		...QUOTAS.map((quota) => ({
			id: quota,
			header: t('admin-ranking:not_admitted.quota', {
				quota: quota === 'q30' ? '30%' : '70%'
			}),
			cell: (item: NotAdmitted) => (
				<span className='truncate text-muted-foreground'>
					{t(
						`admin-ranking:not_admitted.reason.${reasonOf(item[quota].reason)}`
					)}
				</span>
			),
			minWidth: 220,
			flex: 2
		}))
	]
	return (
		<DataTable
			className='max-h-[70vh]'
			columns={columns}
			data={items.map((item) => ({ ...item, id: item.submissionId }))}
			enableColumnMenu={false}
			pagination={pagination}
			state={{ loading, empty: t('admin-ranking:not_admitted.empty') }}
		/>
	)
}

const KNOWN_REASONS = [
	'iseeu_above',
	'ispeu_above',
	'offsite_not_met',
	'cfu_below_min',
	'missing_data',
	'excluded_by_student'
] as const

/** The reason arrives as the code of the rules; one this screen does not know reads as a generic one. */
function reasonOf(reason: string | null) {
	return KNOWN_REASONS.find((known) => known === reason) ?? 'other'
}

/**
 * The applications left out because the same person has another valid one,
 * each with the protocol of the one that counts, in the shared table. With
 * `residenceId` a name opens the application.
 */
function ExcludedRows({
	items,
	residenceId,
	pagination,
	loading
}: {
	items: RankingControlExcluded[]
	residenceId?: string
} & Paging) {
	const { t } = useTranslation()
	const columns: DataTableColumn<RankingControlExcluded & { id: string }>[] =
		[
			{
				id: 'name',
				header: t('admin-ranking:columns.name'),
				cell: (item) => <Name residenceId={residenceId} row={item} />,
				minWidth: 200,
				flex: 2
			},
			{
				id: 'protocol',
				header: t('admin-ranking:columns.protocol'),
				cell: (item) => (
					<span className='tabular-nums'>{item.protocol}</span>
				),
				minWidth: 130,
				flex: 1
			},
			{
				id: 'standing',
				header: t('admin-ranking:columns.standing'),
				cell: (item) => (
					<span className='tabular-nums'>
						{item.standingProtocol}
					</span>
				),
				minWidth: 160,
				flex: 1
			}
		]
	return (
		<div className='flex flex-col gap-3'>
			<p className='text-muted-foreground text-sm'>
				{t('admin-ranking:excluded.subtitle')}
			</p>
			{!pagination && items.length === 0 ? (
				<p className='text-muted-foreground text-sm'>
					{t('admin-ranking:excluded.empty')}
				</p>
			) : (
				<DataTable
					className='max-h-[70vh]'
					columns={columns}
					data={items.map((item) => ({
						...item,
						id: item.submissionId
					}))}
					enableColumnMenu={false}
					pagination={pagination}
					state={{
						loading,
						empty: t('admin-ranking:excluded.empty')
					}}
				/>
			)}
		</div>
	)
}

/** The versions of the ranking, the newest first, with the reason and whether each was published. */
function VersionRows({ versions }: { versions: RankingControl['versions'] }) {
	const { t, i18n } = useTranslation()
	return (
		<ul className='flex flex-col divide-y divide-border'>
			{[...versions].reverse().map((version) => (
				<li
					className='flex flex-wrap items-center justify-between gap-2 py-2 text-sm'
					key={version.number}
				>
					<span>
						<span className='font-medium'>
							{t('admin-ranking:header.version')} {version.number}
						</span>
						<span className='ml-2 text-muted-foreground'>
							{t(`admin-ranking:reason.${version.reason}`)} ·{' '}
							{formatRomeMoment(
								version.computedAt,
								i18n.language
							)}
						</span>
					</span>
					<Badge
						variant={version.publishedAt ? 'success' : 'neutral'}
					>
						{version.publishedAt
							? t('admin-ranking:versions.published')
							: t('admin-ranking:versions.unpublished')}
					</Badge>
				</li>
			))}
		</ul>
	)
}

export { ExcludedRows, NotAdmittedRows, VersionRows }
